import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface ChecklistItem {
  id: string;
  task_id: string;
  title: string;
  completed: boolean;
  sort_order: number;
  created_at: string;
}

export function useTaskChecklist(taskId: string | null) {
  return useQuery({
    queryKey: ['task-checklist', taskId],
    enabled: !!taskId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('task_checklist_items')
        .select('*')
        .eq('task_id', taskId!)
        .order('sort_order')
        .order('created_at');
      if (error) throw error;
      return data as ChecklistItem[];
    },
  });
}

export function useAddChecklistItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ task_id, title }: { task_id: string; title: string }) => {
      const { data, error } = await supabase
        .from('task_checklist_items')
        .insert({ task_id, title })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['task-checklist', vars.task_id] });
      qc.invalidateQueries({ queryKey: ['task-checklist-counts'] });
    },
  });
}

export function useToggleChecklistItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, completed, task_id }: { id: string; completed: boolean; task_id: string }) => {
      const { error } = await supabase
        .from('task_checklist_items')
        .update({ completed })
        .eq('id', id);
      if (error) throw error;
      return { task_id };
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['task-checklist', data.task_id] });
      qc.invalidateQueries({ queryKey: ['task-checklist-counts'] });
    },
  });
}

export function useDeleteChecklistItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, task_id }: { id: string; task_id: string }) => {
      const { error } = await supabase
        .from('task_checklist_items')
        .delete()
        .eq('id', id);
      if (error) throw error;
      return { task_id };
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['task-checklist', data.task_id] });
      qc.invalidateQueries({ queryKey: ['task-checklist-counts'] });
    },
  });
}

export function useChecklistCounts() {
  return useQuery({
    queryKey: ['task-checklist-counts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('task_checklist_items')
        .select('task_id, completed');
      if (error) throw error;
      const counts: Record<string, { total: number; done: number }> = {};
      (data || []).forEach((item: any) => {
        if (!counts[item.task_id]) counts[item.task_id] = { total: 0, done: 0 };
        counts[item.task_id].total++;
        if (item.completed) counts[item.task_id].done++;
      });
      return counts;
    },
  });
}
